import { c as _c } from "react/compiler-runtime";
import { useAIChatStore } from "@/stores/aiChatStore";
import { useRunExecutionStore } from "@/stores/runExecutionStore";
import type { NodeExecutionState } from "@/types/runStream";
import { ERROR_DETAILS_END_MARKER, ERROR_DETAILS_START_MARKER } from "@/utils/parseErrorDetailsBlock";
import { useCallback } from "react";

/**
 * Error context passed from the node that triggered "Fix with AI".
 * Used to build a human-readable message with structured error details.
 */
export interface FixWithAIErrorContext {
  /** Display name of the failed node */
  nodeName?: string;
  /** Action/trigger type (e.g., "send-erc20-token") */
  nodeType?: string;
  /** Error message from execution */
  errorMessage?: string;
}

/** Escape `---` sequences in untrusted strings to prevent delimiter injection. */
function sanitize(str: string): string {
  return str.replace(/---/g, "\\-\\-\\-");
}

/**
 * Build a simple one-line summary sent to the backend as the user message.
 */
function buildSummaryMessage(errorCtx?: FixWithAIErrorContext): string {
  if (errorCtx?.nodeName) {
    const typeHint = errorCtx.nodeType ? ` (${sanitize(errorCtx.nodeType)})` : "";
    return `My workflow run failed at the "${sanitize(errorCtx.nodeName)}" node${typeHint}. Please analyze and fix the workflow.`;
  }
  return "My workflow run failed. Please analyze and fix the workflow.";
}

/**
 * Build a human-readable message with a structured error details block.
 *
 * Format:
 *   My workflow run failed at the "Node Name" node (send-erc20-token).
 *
 *   --- error details ---
 *   Node: Node Name
 *   Type: send-erc20-token
 *   Error: insufficient funds for gas
 *   Run: run_abc123
 *   Timeline:
 *     [SUCCESS] node-0 (webhook)
 *     [FAILURE] node-1 (send-erc20-token)
 *   --- end error details ---
 *
 *   Please analyze and fix the workflow.
 */
function buildErrorMessage(runId: string, errorCtx?: FixWithAIErrorContext, allNodeStates?: Record<string, NodeExecutionState>): string {
  // Human-readable summary line
  let summary: string;
  if (errorCtx?.nodeName) {
    const typeHint = errorCtx.nodeType ? ` (${sanitize(errorCtx.nodeType)})` : "";
    summary = `My workflow run failed at the "${sanitize(errorCtx.nodeName)}" node${typeHint}.`;
  } else {
    summary = "My workflow run failed.";
  }

  // Build structured error details block
  const details: string[] = [];
  if (errorCtx?.nodeName) {
    details.push(`Node: ${sanitize(errorCtx.nodeName)}`);
  }
  if (errorCtx?.nodeType) {
    details.push(`Type: ${sanitize(errorCtx.nodeType)}`);
  }
  if (errorCtx?.errorMessage) {
    details.push(`Error: ${sanitize(errorCtx.errorMessage)}`);
  }
  details.push(`Run: ${sanitize(runId)}`);

  // Build timeline from all node states, sorted chronologically
  if (allNodeStates && Object.keys(allNodeStates).length > 0) {
    details.push("Timeline:");
    const entries = Object.entries(allNodeStates).sort(([, a], [, b]) => {
      if (!a.startedAt || !b.startedAt) return 0;
      return new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime();
    });
    for (const [nodeId, state] of entries) {
      const statusTag = state.status.toUpperCase();
      details.push(`  [${statusTag}] ${sanitize(nodeId)}`);
    }
  }
  const errorBlock = `${ERROR_DETAILS_START_MARKER}\n${details.join("\n")}\n${ERROR_DETAILS_END_MARKER}`;
  return `${summary}\n\n${errorBlock}\n\nPlease analyze and fix the workflow.`;
}

/**
 * Custom hook to handle "Fix with AI" functionality for failed workflow runs.
 * Builds a human-readable message with structured error context visible to the user.
 *
 * @param onDeselectNode - Optional callback to deselect the current node (closes node editor sidebar)
 * @returns handleFixWithAI callback that accepts optional error context
 */
export function useFixWithAI(onDeselectNode) {
  const $ = _c(4);
  const currentRun = useRunExecutionStore(_temp);
  const openPanel = useAIChatStore(_temp2);
  let t0;
  if ($[0] !== currentRun || $[1] !== onDeselectNode || $[2] !== openPanel) {
    t0 = errorCtx => {
      if (currentRun?.runId) {
        onDeselectNode?.();
        const summary = buildSummaryMessage(errorCtx);
        const displayMessage = buildErrorMessage(currentRun.runId, errorCtx, currentRun.nodeStates);
        useAIChatStore.setState({
          pendingMessage: summary,
          pendingErrorBlock: displayMessage
        });
        openPanel();
      }
    };
    $[0] = currentRun;
    $[1] = onDeselectNode;
    $[2] = openPanel;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const handleFixWithAI = t0;
  return handleFixWithAI;
}
function _temp2(state_0) {
  return state_0.openPanel;
}
function _temp(state) {
  return state.currentRun;
}